'use client';

import { useState, useTransition } from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslations } from 'next-intl';
import { CatInputField } from 'blocks-nextjs/blocks-react/components/InputField';
import { CatButton } from 'blocks-nextjs/blocks-react/components/Button';
import { CatToggle } from '@blocks-react/components/Toggle';
import { MAXLENGTH, CREATE_LIST_MODAL_FIELDS } from './constants/common';
import CreateListButtonComponent from '@cat-ecom/pcc-components/src/components/common/saved-list/create-list-button';
import CreateListModalComponent from '@cat-ecom/pcc-components/src/components/common/saved-list/create-list-modal';
import { Conditional, MyEquipmentErrorBanner } from '@cat-ecom/pcc-components';
import { CatLoadingIndicator } from 'components/blocks-components';
import { CreateListProps, FormValues } from './types';
import { CreateListFileUpload } from './create-list-file-upload';
import { useFeedback } from 'providers/feedback-provider';
import { useCreateListModal } from 'providers/create-list-modal-provider';
import EquipmentDropdown from './equipment-dropdown/equipment-dropdown';
import { MyEquipmentList } from 'components/my-equipment/types';
import Overlay from './equipment-dropdown/Overlay';
import { useSavedList } from 'app/[lang]/[esite]/(core)/account/(with-nav)/saved-lists/saved-lists-context';
import { EQUIPMENT_VALIDATION_REGEX as SAVED_LIST_NAME_VALIDATION } from 'components/order-table-row/constants';
import styles from './create-list-modal.module.scss';

export const CreateListModal = ({
  createAction,
  openButtonText,
  openButtonVariant,
  isCspUser,
  isDealerUser = false,
  myEquipmentList,
  selectedEquipmentIndex,
  isError,
  isInstantAccessUser,
  isCsrUser,
  isAssociateEquipmentToSavedList,
  showCreateListBtn = true
}: CreateListProps) => {
  const t = useTranslations();
  const [equipmentSearch, setEquipmentSearch] = useState<string>('');
  const { openModal, closeModal, setModal, showModal } = useCreateListModal();
  const { updateFeedback, showFeedback, dismissFeedback } = useFeedback();
  const [isPending, startTransition] = useTransition();
  const {
    setShowSkeletonLoader,
    minimizeSpecialCharactersFeatureFlag = false
  } = useSavedList() || {};
  const displayListTypeToggle =
    !isCsrUser && !isDealerUser && (isCspUser || !isInstantAccessUser);
  const methods = useForm<FormValues>({
    defaultValues: {
      [CREATE_LIST_MODAL_FIELDS.LIST_NAME]: '',
      [CREATE_LIST_MODAL_FIELDS.LIST_TYPE]: false,
      [CREATE_LIST_MODAL_FIELDS.FILE_NAME]: [],
      [CREATE_LIST_MODAL_FIELDS.EQUIPMENT]: null
    }
  });

  const {
    register,
    setValue,
    getValues,
    reset,
    watch,
    setError,
    clearErrors,
    formState: { errors },
    handleSubmit
  } = methods;

  const equipmentDropdownValue = watch(CREATE_LIST_MODAL_FIELDS.EQUIPMENT);
  const hasEquipmentSelected = selectedEquipmentIndex > -1;
  const selectedEquipment =
    (hasEquipmentSelected && myEquipmentList[selectedEquipmentIndex]) ||
    ({} as MyEquipmentList);

  const shouldOpenModal = () => {
    dismissFeedback();
    openModal();
  };

  const onModalClose = () => {
    dismissFeedback();
    closeModal();
    reset();
  };

  const onSubmit: SubmitHandler<FormValues> = async (data, event) => {
    setEquipmentSearch('');
    dismissFeedback();
    setShowSkeletonLoader(true);
    startTransition(async () => {
      const response = await createAction(
        data[CREATE_LIST_MODAL_FIELDS.LIST_TYPE],
        data[CREATE_LIST_MODAL_FIELDS.LIST_NAME],
        data[CREATE_LIST_MODAL_FIELDS.FILE_NAME][0],
        data[CREATE_LIST_MODAL_FIELDS.EQUIPMENT]
      );
      if (!response.error) {
        if (hasEquipmentSelected && !equipmentDropdownValue) {
          setValue(CREATE_LIST_MODAL_FIELDS.EQUIPMENT, {
            serialNumber: selectedEquipment?.serialNumber,
            model: selectedEquipment?.model,
            assetName: selectedEquipment?.assetName,
            id: selectedEquipment?.id,
            imageURL: selectedEquipment?.imageURL,
            isCommercialEngine: selectedEquipment?.isCommercialEngine,
            isSerialAndModelValid: selectedEquipment?.isSerialAndModelValid,
            isValidatedAsset: selectedEquipment?.isValidatedAsset,
            partNumber: selectedEquipment?.partNumber,
            productFamily: selectedEquipment?.productFamily,
            uniqueIndex: selectedEquipment?.uniqueIndex,
            unvalidatedEquipmentId: selectedEquipment?.unvalidatedEquipmentId,
            unvalidatedItemListId: selectedEquipment?.unvalidatedItemListId,
            seoUrl: selectedEquipment?.seoUrl
          });
        }
        onModalClose();
      } else {
        updateFeedback('Error');
      }
    });
  };

  return (
    <FormProvider {...methods}>
      <CreateListModalComponent
        modal={showModal}
        setModal={setModal}
        modalHeading={t('REQUISITIONLIST_ADD_NEW')}
        closeModal={reset}
        className={`${styles['modal-heading']}`}
      >
        {isPending && <Overlay />}

        <Conditional test={showFeedback}>
          <div className="pb-4">
            <MyEquipmentErrorBanner content={t('UNKONWN_ERROR')} />
          </div>
        </Conditional>
        <form onSubmit={handleSubmit(onSubmit)} data-testid="cat-modal-form">
          {/* TODO - Input Validation - Ensure that <,>,/,\ characters and <script> tags are not allowed to type/paste. */}
          <CatInputField
            {...register(CREATE_LIST_MODAL_FIELDS.LIST_NAME, {
              required: { value: true, message: t('LIST_NAME_REQUIRED') },
              validate: (value) => {
                if (value.trim().length === 0) return t('LIST_NAME_REQUIRED');
              }
            })}
            className="pb-3"
            label={t('ENTER_SAVED_LIST_NAME')}
            notation={t('REQUIRED_FIELD_MESSAGE')}
            maxlength={MAXLENGTH}
            fieldNote={errors?.[CREATE_LIST_MODAL_FIELDS.LIST_NAME]?.message}
            status={
              errors?.[CREATE_LIST_MODAL_FIELDS.LIST_NAME] ? 'error' : undefined
            }
            onInput={(e) => {
              const { value } = e.target as HTMLInputElement;
              setValue(CREATE_LIST_MODAL_FIELDS.LIST_NAME, value);
              if (
                minimizeSpecialCharactersFeatureFlag &&
                !SAVED_LIST_NAME_VALIDATION.test(value)
              ) {
                setError(CREATE_LIST_MODAL_FIELDS.LIST_NAME, {
                  message: t('INVALID_NAME')
                });
              } else {
                clearErrors([CREATE_LIST_MODAL_FIELDS.LIST_NAME]);
              }
            }}
            min={0}
            max={MAXLENGTH}
            required
            data-testid="create-list-input-field"
          />
          <Conditional test={isAssociateEquipmentToSavedList}>
            <hr className={`${styles['gray-line']} opacity-100`} />
            <div className="cat-u-theme-typography-label pt-3">
              {t('LIST_DETAILS_SETTINGS')}
            </div>
            <div className="pt-3">
              <EquipmentDropdown
                myEquipmentList={myEquipmentList}
                selectedEquipmentIndex={selectedEquipmentIndex}
                isError={isError}
                equipmentSearch={equipmentSearch}
                setEquipmentSearch={setEquipmentSearch}
              />
            </div>
            <hr className={`${styles['modal-gray-line']} my-4 opacity-100`} />
          </Conditional>
          <Conditional test={displayListTypeToggle}>
            <Conditional test={isAssociateEquipmentToSavedList}>
              <div className="cat-u-theme-typography-label-sm">
                {t('LIST_VISIBILITY')}
              </div>
            </Conditional>
            <div
              className={`${styles['list-desc']} u-fs--12 d-flex align-items-center text-left pt-1 pb-4`}
            >
              <span className="pe-3 cat-u-theme-typography-footnote">{`${t(
                'LISTS_DEFAULT_PRIVATE'
              )} ${t('PUBLIC_TO_SHARE')}`}</span>
              <div className="pe-2">
                <CatToggle
                  {...register(CREATE_LIST_MODAL_FIELDS.LIST_TYPE)}
                  hideLabel
                  onBlToggleChange={(e: Event) => {
                    const target = e.target as HTMLInputElement;
                    setValue(
                      CREATE_LIST_MODAL_FIELDS.LIST_TYPE,
                      target.checked
                    );
                  }}
                  checked={getValues(CREATE_LIST_MODAL_FIELDS.LIST_TYPE)}
                />
              </div>
              <span className={`${styles['toggle-desc']}`}>{t('PUBLIC')}</span>
            </div>
            <Conditional test={isAssociateEquipmentToSavedList}>
              <hr className={`${styles['gray-line']} opacity-100`} />
            </Conditional>
          </Conditional>
          <CreateListFileUpload
            isAssociateEquipmentToSavedList={isAssociateEquipmentToSavedList}
          />
          <CatButton
            type="submit"
            slot="after"
            className="text-nowrap"
            disabled={
              !!errors?.[CREATE_LIST_MODAL_FIELDS.LIST_NAME] || isPending
            }
          >
            <Conditional test={isPending}>
              <CatLoadingIndicator slot={'before'} small={true} />
            </Conditional>
            {isPending ? t('CREATING_LIST') : t('CREATE_SAVE')}
          </CatButton>
          <CatButton
            className="cat-u-padding-right-sm"
            variant="ghost"
            onClick={onModalClose}
          >
            {t('REQUISITIONLIST_BTN_CANCEL')}
          </CatButton>
        </form>
      </CreateListModalComponent>

      {showCreateListBtn && (
        <CreateListButtonComponent
          setModal={shouldOpenModal}
          buttonHeading={t(openButtonText)}
          buttonVariant={openButtonVariant}
        />
      )}
    </FormProvider>
  );
};
